import { Container, AppBar, Toolbar, Typography, IconButton, Box } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useParams } from 'react-router-dom';
import type { CouponValidationResult } from '@lov/shared';
import { useBookingProgress } from '../../hooks/useBookingProgress';
import { useBookingSessionPersistence } from '../../hooks/useBookingSessionPersistence';
import { BookingSection } from './BookingSection';
import { VenueSelectionSection } from './sections/VenueSelectionSection';
import { DateTimeSection } from './sections/DateTimeSection';
import { InventorySelectionSection } from './sections/InventorySelectionSection';
import type { SelectedInventoryItem } from './sections/InventorySelectionSection';
import { getFirstSelectedVenueInventoryId } from './sections/InventorySelectionSection';
import { AddonsSection } from './sections/AddonsSection';
import { GuestDetailsSection } from './sections/GuestDetailsSection';
import { ReviewSection } from './sections/ReviewSection';
import { StickyCTA } from './StickyCTA';
import { useRef, useCallback, useState, useEffect } from 'react';
import { useVenue } from '../../../venues/hooks/useVenues';
import { useCreateBooking } from '../../hooks/useBookings';
import { useMe } from '../../../../hooks/auth/useMe';
import { useFeatureFlag } from '../../../../hooks/useFeatureFlag';
import { usePricingRates } from '../../../settings/hooks/useSettings';
import { useSlotLock, useUnlockSlot } from '../../hooks/useSlotLock';
import type { UnlockSlotParams } from '../../hooks/useSlotLock';
import { SlotUnavailableModal } from '../SlotUnavailableModal';
import { useVenueAvailability } from '../../hooks/useBookings';
import type { AlternativeSlot, InventorySlotAvailability } from '@lov/shared';

interface BookingProgressiveFlowProps {
  venueId?: string; // Pre-selected venue from URL
  basePrice?: number; // Base price for time slot calculations
}

/**
 * Single source of truth for addon resolution in inventory mode.
 * Both review display and payment payload use this — never build addon lists independently.
 */
function buildResolvedAddons(
  selectedAddons: Map<string, number>,
  inventoryAvailability: InventorySlotAvailability[]
): Array<{
  addonItemId: string;
  venueInventoryId: string;
  quantity: number;
  name?: string;
  pricePerHour?: number;
  pricePerHalfHour?: number;
}> {
  const venueInventoryLookup = new Map<string, string>();
  const detailsLookup = new Map<
    string,
    { addonName: string; pricePerHour?: number; pricePerHalfHour?: number }
  >();

  for (const invItem of inventoryAvailability) {
    for (const addon of invItem.addons ?? []) {
      if (addon.venueInventoryId) {
        venueInventoryLookup.set(addon.addonItemId, addon.venueInventoryId);
      }
      if (!detailsLookup.has(addon.addonItemId)) {
        detailsLookup.set(addon.addonItemId, {
          addonName: addon.addonName,
          pricePerHour: addon.pricePerHour,
          pricePerHalfHour: addon.pricePerHalfHour,
        });
      }
    }
  }

  return Array.from(selectedAddons.entries())
    .filter(([, qty]) => qty > 0)
    .map(([addonItemId, quantity]) => {
      const venueInventoryId = venueInventoryLookup.get(addonItemId) ?? '';
      if (!venueInventoryId) {
        console.warn(
          `[Booking] Addon ${addonItemId} missing venueInventoryId — excluded from booking`
        );
      }
      const details = detailsLookup.get(addonItemId);
      return {
        addonItemId,
        venueInventoryId,
        quantity,
        name: details?.addonName,
        pricePerHour: details?.pricePerHour,
        pricePerHalfHour: details?.pricePerHalfHour,
      };
    })
    .filter((a) => a.venueInventoryId !== '');
}

export function BookingProgressiveFlow({ venueId, basePrice = 0 }: BookingProgressiveFlowProps) {
  const navigate = useNavigate();
  const { stateSlug, citySlug, venueSlug } = useParams<{
    stateSlug?: string;
    citySlug?: string;
    venueSlug?: string;
  }>();
  const createBookingMutation = useCreateBooking();

  // Fetch current user for auto-skip guest details
  const { data: user } = useMe();

  // Fetch venue data if venueId is provided
  const { data: venue } = useVenue(venueId || '');

  // Fetch pricing rates for service fee and GST calculation
  const { data: pricingRates } = usePricingRates();

  // Feature flag for food ordering
  const { isEnabled: foodOrderingEnabled } = useFeatureFlag('food-ordering');

  // Derive hasFoodMenu from venue data (only count available items)
  const hasFoodMenu = (venue as { _count?: { foodItems?: number } })?._count?.foodItems
    ? // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      (venue as { _count?: { foodItems?: number } })._count!.foodItems! > 0
    : false;

  // Initialize progress with venue pre-completed if venueId is provided.
  // When venueId is given but venue data hasn't loaded yet, we still pre-mark the section
  // as completed so that the first visible step is datetime (not venue).
  // Food ordering is now handled on its own dedicated page (/bookings/:id/food).
  // Always pass hasFoodMenu: false so the progress hook never inserts a food step
  // into the wizard. hasFoodMenu/foodOrderingEnabled are still used in handlePayNow
  // to decide whether to redirect to the food page.
  const progress = useBookingProgress(
    venueId
      ? {
          initialCompletedSections: ['venue'],
          initialSectionData: { venue: { venueId } },
          // Use venue name once loaded, otherwise fall back to a placeholder so the hook
          // treats the section as completed from the very first render.
          initialSectionSummaries: { venue: venue?.name ?? '' },
          venuePreSelected: true,
          hasFoodMenu: false,
          foodOrderingEnabled: false,
        }
      : { hasFoodMenu: false, foodOrderingEnabled: false }
  );

  const session = useBookingSessionPersistence();
  const [isCreatingBooking, setIsCreatingBooking] = useState(false);

  // Addon selection state (Map for easier lookup and updates)
  const [selectedAddons, setSelectedAddons] = useState<Map<string, number>>(new Map());

  // Inventory selection state (Map<venueInventoryId, { quantity, pricePerHour }>)
  const [selectedInventory, setSelectedInventory] = useState<Map<string, SelectedInventoryItem>>(
    new Map()
  );

  // Coupon state
  const [appliedCoupon, setAppliedCoupon] = useState<CouponValidationResult | null>(null);

  // Lock acquisition state
  const slotLock = useSlotLock();
  const unlockSlot = useUnlockSlot();
  const [activeLockInfo, setActiveLockInfo] = useState<UnlockSlotParams | null>(null);
  const [showUnavailableModal, setShowUnavailableModal] = useState(false);
  const [alternatives, setAlternatives] = useState<AlternativeSlot[]>([]);
  const [unavailableError, setUnavailableError] = useState('');

  // Ref to store the current section's continue callback
  const sectionContinueRef = useRef<(() => void) | null>(null);

  // Derive datetime section data for downstream use
  const datetimeData = progress.sectionData.datetime as
    | {
        date?: string;
        startTime?: string;
        endTime?: string;
        durationMinutes?: number;
        inventoryId?: string;
      }
    | undefined;

  // Fetch availability for the selected date so we know bookingMode
  // This powers the inventory section and also used for bookingMode detection
  const { data: availabilityData } = useVenueAvailability(
    (progress.sectionData.venue as { venueId?: string })?.venueId || venueId || '',
    {
      date: datetimeData?.date || '',
      startTime: datetimeData?.startTime,
      endTime: datetimeData?.endTime,
    }
  );

  // Sync bookingMode into progress hook once availability data arrives
  useEffect(() => {
    if (availabilityData?.bookingMode && availabilityData.bookingMode !== progress.bookingMode) {
      progress.setBookingMode(availabilityData.bookingMode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availabilityData?.bookingMode, progress.bookingMode, progress.setBookingMode]);

  // Auto-skip guest details section when user has profile data (name + phone)
  // Fires once after datetime is completed, before the user reaches the guests section
  const hasAutoSkippedGuests = useRef(false);
  const completeSectionRef = useRef(progress.completeSection);
  useEffect(() => {
    completeSectionRef.current = progress.completeSection;
  });

  useEffect(() => {
    if (
      progress.completedSections.includes('datetime') &&
      !progress.completedSections.includes('guests') &&
      user?.firstName &&
      user?.phone &&
      !hasAutoSkippedGuests.current
    ) {
      hasAutoSkippedGuests.current = true;
      completeSectionRef.current(
        'guests',
        {
          guestName: `${user.firstName} ${user.lastName || ''}`.trim(),
          guestPhone: user.phone,
          guestCount: 1,
          notes: '',
        },
        `1 guest (${user.firstName})`
      );
    }
  }, [progress.completedSections, user]);

  // When venueId is pre-selected and venue data loads, update the venue section summary
  // so downstream steps (e.g. ReviewSection) see the correct venue name.
  useEffect(() => {
    if (venueId && venue && !progress.sectionSummaries.venue) {
      progress.completeSection('venue', { venueId }, venue.name);
    }
  }, [venueId, venue, progress]);

  // Inventory availability items from the availability response
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const inventoryAvailability: InventorySlotAvailability[] =
    availabilityData?.inventoryAvailability ?? [];

  // Handler to update a single addon quantity (used in inventory mode embedded addons)
  const handleAddonQuantityUpdate = useCallback((addonItemId: string, quantity: number) => {
    setSelectedAddons((prev) => {
      const next = new Map(prev);
      if (quantity <= 0) {
        next.delete(addonItemId);
      } else {
        next.set(addonItemId, quantity);
      }
      return next;
    });
  }, []);

  // Handler to update a single inventory item quantity
  const handleInventoryUpdate = useCallback(
    (venueInventoryId: string, quantity: number) => {
      setSelectedInventory((prev) => {
        const next = new Map(prev);
        if (quantity <= 0) {
          next.delete(venueInventoryId);
        } else {
          const item = inventoryAvailability.find((i) => i.venueInventoryId === venueInventoryId);
          const pricePerHour =
            item?.pricePerHour ?? (item?.pricePerHalfHour ? item.pricePerHalfHour * 2 : 0);
          next.set(venueInventoryId, { quantity, pricePerHour });
        }
        return next;
      });
    },
    [inventoryAvailability]
  );

  // Register section continue callback (called by each section when mounted/active)
  const registerSectionContinue = useCallback((callback: () => void) => {
    sectionContinueRef.current = callback;
  }, []);

  const reviewData = progress.sectionData.review as { totalPrice?: number } | undefined;
  const totalPrice = reviewData?.totalPrice ?? 0;

  // Enable Pay Now when required sections complete
  // Guest Details is optional - only venue, datetime, and review are required
  const isReadyToPay =
    progress.completedSections.includes('venue') &&
    progress.completedSections.includes('datetime') &&
    progress.completedSections.includes('review');

  // Dynamic CTA label based on current section
  const getCTALabel = () => {
    if (isReadyToPay) return hasFoodMenu && foodOrderingEnabled ? 'Next →' : 'Pay Now →';
    const guestSectionData = progress.sectionData.guests as { guestCount?: number } | undefined;
    const guestCount = guestSectionData?.guestCount ?? 1;
    switch (progress.currentSection) {
      case 'datetime':
        return 'Continue';
      case 'addons':
        return selectedAddons.size > 0
          ? `Continue with ${selectedAddons.size} add-on${selectedAddons.size > 1 ? 's' : ''}`
          : 'Skip & Continue';
      case 'guests':
        return guestCount === 1 ? 'Skip & Continue' : 'Continue';
      case 'inventory':
        return 'Continue';
      default:
        return 'Continue';
    }
  };

  // Dynamic hint text based on current section state
  const getCTAHint = (): string | undefined => {
    if (isReadyToPay) return undefined;
    const datetimeD = progress.sectionData.datetime as
      | {
          date?: string;
          startTime?: string;
          endTime?: string;
        }
      | undefined;
    switch (progress.currentSection) {
      case 'datetime':
        if (!datetimeD?.date) return 'Select a date to continue';
        if (!datetimeD?.startTime) return 'Pick a start time';
        if (!datetimeD?.endTime) return 'Choose your duration';
        return undefined;
      case 'addons':
        return 'Add-ons are optional';
      case 'guests':
        return 'Guest details are optional';
      case 'inventory':
        return 'Select at least one item';
      default:
        return undefined;
    }
  };

  // Dynamic status text
  const getStatusText = (): string => {
    if (isReadyToPay) return 'Ready to pay!';
    if (progress.completedSections.includes('datetime')) return 'Almost there!';
    switch (progress.currentSection) {
      case 'datetime':
        return 'Step 1: Date & Time';
      case 'venue':
        return 'Step 1: Select Venue';
      default:
        return 'In progress...';
    }
  };

  // Handle alternative slot selection
  const handleSelectAlternative = (slot: AlternativeSlot) => {
    // Update datetime section with new time
    const currentDatetimeData = progress.sectionData.datetime as {
      date?: string;
      inventoryId?: string;
      durationMinutes?: number;
    };

    const updatedData = {
      ...currentDatetimeData,
      startTime: slot.startTime,
      endTime: slot.endTime,
    };

    // Update section data
    progress.sectionData.datetime = updatedData;
    session.updateSectionData('datetime', updatedData);

    // Update summary
    const summary = `${currentDatetimeData.date} | ${slot.startTime} - ${slot.endTime}`;
    progress.sectionSummaries.datetime = summary;

    // Close modal
    setShowUnavailableModal(false);
    setAlternatives([]);
  };

  // Resolve venueInventoryId for addons:
  // - In inventory mode: use the first selected inventory item's venueInventoryId
  // - In capacity mode: use the inventoryId from datetime section (first available inventory)
  const addonVenueInventoryId =
    progress.bookingMode === 'inventory'
      ? (getFirstSelectedVenueInventoryId(selectedInventory) ?? '')
      : (datetimeData?.inventoryId ?? '');

  // Handle booking creation and payment navigation
  const handlePayNow = async () => {
    if (!isReadyToPay || isCreatingBooking) {
      // If not ready, trigger the current section's continue
      if (sectionContinueRef.current) {
        sectionContinueRef.current();
      }
      return;
    }

    try {
      setIsCreatingBooking(true);

      // Gather booking data from all sections
      const venueData = progress.sectionData.venue as { venueId?: string };
      const bookingDatetimeData = progress.sectionData.datetime as {
        date?: string;
        startTime?: string;
        endTime?: string;
        inventoryId?: string;
      };
      const guestData = progress.sectionData.guests as {
        guestCount?: number;
        guestName?: string;
        guestPhone?: string;
        notes?: string;
      };
      // Build addons array from the selectedAddons Map (always current, covers both capacity and
      // inventory mode).
      //
      // Capacity mode: AddonsSection serializes correct addonItemId + venueInventoryId into
      //   progress.sectionData.addons. Use that as the authoritative source.
      // Inventory mode: InventorySelectionSection embeds addons but never calls onComplete with
      //   addons. The Map key = addonItemId (catalog UUID). Resolve venueInventoryId by looking
      //   up the addon's VenueInventory from inventoryAvailability (now includes venueInventoryId).
      let addonsForPayload: Array<{
        addonItemId: string;
        venueInventoryId: string;
        quantity: number;
      }>;

      if (progress.bookingMode === 'capacity') {
        // Capacity mode: use the serialized addons from AddonsSection.onComplete which already
        // have the correct addonItemId (catalog UUID) and venueInventoryId (addon's VenueInventory).
        const addonData = progress.sectionData.addons as {
          selectedAddons?: Array<{
            addonItemId: string;
            venueInventoryId: string;
            quantity: number;
          }>;
        };
        addonsForPayload = (addonData?.selectedAddons ?? []).filter((a) => a.quantity > 0);
      } else {
        // Inventory mode: use unified addon resolution
        const resolvedAddons = buildResolvedAddons(selectedAddons, inventoryAvailability);
        addonsForPayload = resolvedAddons.map(({ addonItemId, venueInventoryId, quantity }) => ({
          addonItemId,
          venueInventoryId,
          quantity,
        }));
      }

      // Build inventory items for the booking
      // In inventory mode, use selectedInventory map; in capacity mode use datetime inventoryId
      const inventoryItems: Array<{ venueInventoryId: string; quantity: number }> =
        progress.bookingMode === 'inventory'
          ? Array.from(selectedInventory.entries())
              .filter(([, { quantity }]) => quantity > 0)
              .map(([vid, { quantity }]) => ({ venueInventoryId: vid, quantity }))
          : bookingDatetimeData?.inventoryId
            ? [{ venueInventoryId: bookingDatetimeData.inventoryId, quantity: 1 }]
            : [];

      // Acquire lock before payment
      const lockResult = await slotLock.mutateAsync({
        venueId: venueData?.venueId || venueId || '',
        date: bookingDatetimeData?.date || '',
        startTime: bookingDatetimeData?.startTime || '',
        endTime: bookingDatetimeData?.endTime || '',
        guestCount: guestData?.guestCount || 1,
        inventoryItems,
        addonInventoryIds: addonsForPayload.map((a) => a.venueInventoryId),
      });

      if (lockResult.lockId && lockResult.expiresAt) {
        // Capture lock info in a local variable first — React setState is async/batched,
        // so reading activeLockInfo immediately after setActiveLockInfo would return the old value.
        const lockInfoPayload = {
          lockId: lockResult.lockId,
          venueId: venueData?.venueId || venueId || '',
          date: bookingDatetimeData?.date || '',
          startTime: bookingDatetimeData?.startTime || '',
          endTime: bookingDatetimeData?.endTime || '',
          inventoryItems: inventoryItems.map((item) => ({
            venueInventoryId: item.venueInventoryId,
          })),
          addonInventoryIds: addonsForPayload.map((a) => a.venueInventoryId),
        };
        // Store lock info so it can be released if user navigates back before payment
        setActiveLockInfo(lockInfoPayload);

        // Lock acquired - create booking and navigate to payment
        // Get couponCode from applied coupon or from review section data
        const reviewData2 = progress.sectionData.review as { couponCode?: string } | undefined;
        const couponCode =
          (appliedCoupon?.isValid && appliedCoupon.coupon?.code) ||
          reviewData2?.couponCode ||
          undefined;

        const bookingPayload = {
          venueId: venueData?.venueId || venueId || '',
          bookingDate: bookingDatetimeData?.date || '', // API expects bookingDate not date
          startTime: bookingDatetimeData?.startTime || '',
          endTime: bookingDatetimeData?.endTime || '',
          guestCount: guestData?.guestCount || 1,
          specialRequests: guestData?.notes || '', // API expects specialRequests not notes
          inventoryItems,
          // Only include addons if non-empty to avoid sending empty array
          ...(addonsForPayload.length > 0 && { addons: addonsForPayload }),
          // Include coupon code if one is applied
          ...(couponCode && { couponCode }),
        };

        // Navigate to food ordering page if venue has food — defer booking creation until after
        // food selection to avoid creating a PENDING booking that gets cancelled on back navigation
        if (hasFoodMenu && foodOrderingEnabled) {
          session.clearSession();
          setIsCreatingBooking(false);
          navigate('/bookings/new/food', {
            state: {
              bookingPayload,
              lockInfo: lockInfoPayload,
              venueName: venue?.name,
            },
          });
          return;
        }

        // Create booking via API
        const booking = await createBookingMutation.mutateAsync(bookingPayload);

        // Lock is now owned by the payment flow — don't release it on back navigation
        setActiveLockInfo(null);

        // Clear session after successful creation
        session.clearSession();

        if (booking.amountDue && booking.amountDue > 0) {
          navigate(`/payments/select?bookingId=${booking.id}&amount=${booking.amountDue}`);
        } else {
          // No payment needed, go to booking details
          navigate(`/bookings/${booking.id}`);
        }
      } else if (lockResult.alternatives) {
        // Slot unavailable - show alternatives
        setAlternatives(lockResult.alternatives);
        setUnavailableError(lockResult.error || 'Slot no longer available');
        setShowUnavailableModal(true);
        setIsCreatingBooking(false);
      }
    } catch (error) {
      console.error('Failed to acquire lock or create booking:', error);

      // Check if error has alternatives (race condition)
      const apiError = error as {
        response?: { data?: { alternatives?: AlternativeSlot[]; error?: string } };
      };
      if (apiError.response?.data?.alternatives) {
        setAlternatives(apiError.response.data.alternatives);
        setUnavailableError(apiError.response.data.error || 'Slot no longer available');
        setShowUnavailableModal(true);
      }

      setIsCreatingBooking(false);
    }
  };

  // Derive venueId for back navigation
  const currentVenueId = (progress.sectionData.venue as { venueId?: string })?.venueId || venueId;

  // Release any held slot lock (fire-and-forget — best effort)
  const releaseActiveLock = useCallback(() => {
    if (activeLockInfo) {
      unlockSlot.mutate(activeLockInfo);
      setActiveLockInfo(null);
    }
  }, [activeLockInfo, unlockSlot]);

  const handleGoBack = () => {
    releaseActiveLock();
    if (stateSlug && citySlug && venueSlug) {
      navigate(`/${stateSlug}/${citySlug}/${venueSlug}`);
    } else if (currentVenueId) {
      navigate(`/venues/${currentVenueId}`);
    } else {
      navigate('/venues');
    }
  };

  // Section order for back navigation (same as effective order in progress hook)
  const effectiveSectionOrder =
    progress.bookingMode === 'inventory'
      ? (['venue', 'datetime', 'inventory', 'guests', 'review'] as const)
      : (['venue', 'datetime', 'addons', 'guests', 'review'] as const);

  // Handle back button press in sticky CTA — go to previous section
  const handleStickyBack = () => {
    const currentIdx = (effectiveSectionOrder as readonly string[]).indexOf(
      progress.currentSection
    );
    if (currentIdx > 0) {
      const prevSection = effectiveSectionOrder[currentIdx - 1];
      // Release lock when navigating back from review (where Pay Now button lives)
      if (progress.currentSection === 'review') {
        releaseActiveLock();
      }
      // Skip 'venue' section if it's pre-selected
      if (prevSection === 'venue' && venueId) {
        handleGoBack();
      } else {
        progress.expandSection(prevSection);
      }
    } else {
      handleGoBack();
    }
  };

  // Show back button when not on the first visible section
  const firstVisibleSection = venueId ? 'datetime' : 'venue';
  const showStickyBack = progress.currentSection !== firstVisibleSection;

  return (
    <>
      {/* Minimal booking header with back navigation and branding */}
      <AppBar
        position="sticky"
        color="inherit"
        elevation={1}
        sx={{ top: 0, zIndex: (theme) => theme.zIndex.appBar }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', minHeight: 56 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton edge="start" onClick={handleGoBack} aria-label="Back to venue" size="small">
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="body2" color="text.secondary">
              Back to Venue
            </Typography>
          </Box>
          <Typography
            variant="h6"
            fontWeight={700}
            color="primary"
            component="span"
            sx={{ letterSpacing: -0.5 }}
          >
            Lord of Venues
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ pb: 10 }}>
        {/* Venue Selection - Hidden when venueId is pre-provided via route param */}
        {!venueId && (
          <BookingSection
            id="venue"
            title="Select Venue & Activity"
            expanded={progress.currentSection === 'venue'}
            completed={progress.completedSections.includes('venue')}
            summary={progress.sectionSummaries.venue}
            onExpand={() => progress.expandSection('venue')}
            disabled={false}
            stepNumber={1}
            subtitle="Choose your venue and activity"
            showConnector={true}
          >
            <VenueSelectionSection
              initialVenueId={venueId}
              onComplete={(data: unknown, summary: string) => {
                progress.completeSection('venue', data, summary);
                session.updateSectionData('venue', data);
              }}
              registerContinue={
                progress.currentSection === 'venue' ? registerSectionContinue : undefined
              }
            />
          </BookingSection>
        )}

        {/* Date & Time - Visible after venue */}
        {progress.visibleSections.includes('datetime') && (
          <BookingSection
            id="datetime"
            title="Select Date & Time"
            expanded={progress.currentSection === 'datetime'}
            completed={progress.completedSections.includes('datetime')}
            summary={progress.sectionSummaries.datetime}
            onExpand={() => progress.expandSection('datetime')}
            disabled={!progress.completedSections.includes('venue')}
            stepNumber={progress.visibleSections.indexOf('datetime') + 1}
            subtitle="Pick a date and time slot"
            showConnector={true}
          >
            <DateTimeSection
              venueId={(progress.sectionData.venue as { venueId?: string })?.venueId || venueId}
              basePrice={basePrice}
              bookingMode={availabilityData?.bookingMode}
              initialData={
                progress.sectionData.datetime as
                  | {
                      date?: string;
                      startTime?: string;
                      endTime?: string;
                      durationMinutes?: number;
                    }
                  | undefined
              }
              onComplete={(data: unknown, summary: string) => {
                const dtData = data as { bookingMode?: 'capacity' | 'inventory' };
                if (dtData.bookingMode) {
                  progress.setBookingMode(dtData.bookingMode);
                }
                progress.completeSection('datetime', data, summary);
                session.updateSectionData('datetime', data);
              }}
              registerContinue={
                progress.currentSection === 'datetime' ? registerSectionContinue : undefined
              }
            />
          </BookingSection>
        )}

        {/* Inventory Selection - Visible after datetime, only in inventory mode */}
        {progress.visibleSections.includes('inventory') && (
          <BookingSection
            id="inventory"
            title="Select Equipment"
            expanded={progress.currentSection === 'inventory'}
            completed={progress.completedSections.includes('inventory')}
            summary={progress.sectionSummaries.inventory}
            onExpand={() => progress.expandSection('inventory')}
            disabled={!progress.completedSections.includes('datetime')}
            stepNumber={progress.visibleSections.indexOf('inventory') + 1}
            subtitle="Select at least one item"
            showConnector={true}
          >
            <InventorySelectionSection
              inventoryAvailability={inventoryAvailability}
              selectedInventory={selectedInventory}
              selectedStartTime={datetimeData?.startTime ?? null}
              selectedEndTime={datetimeData?.endTime ?? null}
              onUpdateQuantity={handleInventoryUpdate}
              onComplete={(data: unknown, summary: string) => {
                progress.completeSection('inventory', data, summary);
                session.updateSectionData('inventory', data);
              }}
              registerContinue={
                progress.currentSection === 'inventory' ? registerSectionContinue : undefined
              }
              selectedAddons={selectedAddons}
              onUpdateAddonQuantity={handleAddonQuantityUpdate}
            />
          </BookingSection>
        )}

        {/* Add-ons - Visible after datetime (OPTIONAL) */}
        {progress.visibleSections.includes('addons') && (
          <BookingSection
            id="addons"
            title="Add-ons"
            expanded={progress.currentSection === 'addons'}
            completed={progress.completedSections.includes('addons')}
            summary={progress.sectionSummaries.addons}
            onExpand={() => progress.expandSection('addons')}
            disabled={!progress.completedSections.includes('datetime')}
            optional={true}
            stepNumber={progress.visibleSections.indexOf('addons') + 1}
            subtitle="Enhance your booking with extras"
            showConnector={true}
          >
            <AddonsSection
              venueInventoryId={addonVenueInventoryId}
              date={datetimeData?.date || ''}
              startTime={datetimeData?.startTime || ''}
              endTime={datetimeData?.endTime || ''}
              selectedAddons={selectedAddons}
              onAddonsChange={setSelectedAddons}
              onComplete={(data: unknown, summary: string) => {
                progress.completeSection('addons', data, summary);
                session.updateSectionData('addons', data);
              }}
              registerContinue={
                progress.currentSection === 'addons' ? registerSectionContinue : undefined
              }
            />
          </BookingSection>
        )}

        {/* Guest Details - Visible after datetime (OPTIONAL) */}
        {progress.visibleSections.includes('guests') && (
          <BookingSection
            id="guests"
            title="Guest Details"
            expanded={progress.currentSection === 'guests'}
            completed={progress.completedSections.includes('guests')}
            summary={progress.sectionSummaries.guests}
            onExpand={() => progress.expandSection('guests')}
            disabled={!progress.completedSections.includes('datetime')}
            optional={true}
            stepNumber={progress.visibleSections.indexOf('guests') + 1}
            subtitle="Pre-filled from your profile"
            showConnector={true}
          >
            <GuestDetailsSection
              onComplete={(data: unknown, summary: string) => {
                progress.completeSection('guests', data, summary);
                session.updateSectionData('guests', data);
              }}
              registerContinue={
                progress.currentSection === 'guests' ? registerSectionContinue : undefined
              }
              isActive={progress.currentSection === 'guests'}
              initialData={
                progress.sectionData.guests as
                  | {
                      guestName?: string;
                      guestPhone?: string;
                      guestCount?: number;
                      notes?: string;
                    }
                  | undefined
              }
            />
          </BookingSection>
        )}

        {/* Review - Visible after datetime */}
        {progress.visibleSections.includes('review') &&
          (() => {
            // Build booking data for ReviewSection with all required fields
            const reviewDatetimeData = progress.sectionData.datetime as {
              date?: string;
              startTime?: string;
              endTime?: string;
              durationMinutes?: number;
              inventoryId?: string;
            };
            const guestData = progress.sectionData.guests as {
              guestCount?: number;
            };
            const addonData = progress.sectionData.addons as {
              selectedAddons?: Array<{
                addonItemId: string;
                venueInventoryId: string;
                quantity: number;
              }>;
            };

            // Build inventory items with resolved names for the review section
            const selectedInventoryWithNames = Array.from(selectedInventory.entries())
              .filter(([, { quantity }]) => quantity > 0)
              .map(([venueInventoryId, { quantity, pricePerHour }]) => {
                const invItem = inventoryAvailability.find(
                  (inv) => inv.venueInventoryId === venueInventoryId
                );
                return {
                  venueInventoryId,
                  itemName: invItem?.itemName ?? 'Equipment',
                  quantity,
                  pricePerHour,
                };
              });

            // Build selectedAddons for review from the Map state (same resolution as payment)
            let reviewSelectedAddons: Array<{
              addonItemId: string;
              venueInventoryId: string;
              quantity: number;
              name?: string;
              pricePerHour?: number;
              pricePerHalfHour?: number;
            }>;
            if (progress.bookingMode === 'capacity') {
              reviewSelectedAddons = (addonData?.selectedAddons ?? []).filter(
                (a) => a.quantity > 0
              );
            } else {
              // Inventory mode: use same resolution as payment (single source of truth)
              reviewSelectedAddons = buildResolvedAddons(selectedAddons, inventoryAvailability);
            }

            const reviewBookingData = {
              venueId: (progress.sectionData.venue as { venueId?: string })?.venueId || venueId,
              venueName: venue?.name,
              date: reviewDatetimeData?.date,
              startTime: reviewDatetimeData?.startTime,
              endTime: reviewDatetimeData?.endTime,
              durationMinutes: reviewDatetimeData?.durationMinutes || 60,
              basePrice: venue?.basePrice || 0,
              guestCount: guestData?.guestCount || 1,
              // Add settings for price calculation:
              serviceFeePercent: pricingRates?.serviceFeePercent || 0,
              gstRate: pricingRates?.gstPercent || 18,
              gstEnabled: pricingRates?.gstEnabled ?? false,
              // Pass addon data for pricing calculation
              venueInventoryId: addonVenueInventoryId || reviewDatetimeData?.inventoryId,
              selectedAddons: reviewSelectedAddons,
              // Pass booking mode and inventory data for correct pricing display
              bookingMode: progress.bookingMode,
              selectedInventory: selectedInventoryWithNames,
            };

            return (
              <BookingSection
                id="review"
                title="Review & Payment"
                expanded={progress.currentSection === 'review'}
                completed={progress.completedSections.includes('review')}
                summary={progress.sectionSummaries.review}
                onExpand={() => progress.expandSection('review')}
                disabled={!progress.completedSections.includes('datetime')}
                stepNumber={progress.visibleSections.indexOf('review') + 1}
                subtitle="Price breakdown and payment"
              >
                <ReviewSection
                  bookingData={reviewBookingData}
                  appliedCoupon={appliedCoupon}
                  onApplyCoupon={setAppliedCoupon}
                  onRemoveCoupon={() => setAppliedCoupon(null)}
                  onComplete={(data, summary) => {
                    progress.completeSection('review', data, summary);
                    // Don't clear session here - that happens in handlePayNow
                  }}
                  registerContinue={
                    progress.currentSection === 'review' ? registerSectionContinue : undefined
                  }
                />
              </BookingSection>
            );
          })()}

        {/* Sticky CTA - Dynamic label, hint, and status text */}
        <StickyCTA
          label={getCTALabel()}
          price={totalPrice > 0 ? totalPrice : undefined}
          onClick={handlePayNow}
          disabled={!isReadyToPay}
          loading={isCreatingBooking}
          statusText={getStatusText()}
          hint={getCTAHint()}
          dotCount={3}
          dotCompleted={
            progress.completedSections.filter((s) => ['datetime', 'addons', 'review'].includes(s))
              .length
          }
          onBack={handleStickyBack}
          showBack={showStickyBack}
        />

        {/* Slot Unavailable Modal */}
        <SlotUnavailableModal
          open={showUnavailableModal}
          errorMessage={unavailableError}
          alternatives={alternatives}
          totalCapacity={100}
          onSelectSlot={handleSelectAlternative}
          onClose={() => {
            setShowUnavailableModal(false);
            setAlternatives([]);
          }}
        />
      </Container>
    </>
  );
}
